import { Injectable } from '@angular/core';
import { FirebaseRemoteConfig } from '@capacitor-firebase/remote-config';
import { Capacitor } from '@capacitor/core';
import { RemoteConfigValueType } from '../enums';

@Injectable({ providedIn: 'root' })
export class RemoteConfigService {
  private initialized: boolean = false;

  async fetchAndActivate(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      await this.initialize();
      await FirebaseRemoteConfig.fetchAndActivate();
      return true;
    } catch {
      return false;
    }
  }

  async getValue<T>(key: string, type: RemoteConfigValueType, defaultValue: T): Promise<T> {
    if (!Capacitor.isNativePlatform()) {
      return defaultValue;
    }

    switch (type) {
      case RemoteConfigValueType.STRING:
        return (await this.getString(key, defaultValue as string)) as T;
      case RemoteConfigValueType.BOOLEAN:
        return (await this.getBoolean(key, defaultValue as boolean)) as T;
      case RemoteConfigValueType.NUMBER:
        return (await this.getNumber(key, defaultValue as number)) as T;
      case RemoteConfigValueType.JSON:
        return await this.getJson<T>(key, defaultValue);
      default:
        return defaultValue;
    }
  }

  private async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      await FirebaseRemoteConfig.setSettings({
        minimumFetchIntervalInSeconds: 0
      });
      this.initialized = true;
    } catch {}
  }

  private async getString(key: string, defaultValue: string): Promise<string> {
    try {
      const result: { value: string } = await FirebaseRemoteConfig.getString({ key });
      return result.value || defaultValue;
    } catch {
      return defaultValue;
    }
  }

  private async getBoolean(key: string, defaultValue: boolean): Promise<boolean> {
    try {
      const result: { value: boolean } = await FirebaseRemoteConfig.getBoolean({ key });
      return result.value;
    } catch {
      return defaultValue;
    }
  }

  private async getNumber(key: string, defaultValue: number): Promise<number> {
    try {
      const result: { value: number } = await FirebaseRemoteConfig.getNumber({ key });
      return result.value;
    } catch {
      return defaultValue;
    }
  }

  private async getJson<T>(key: string, defaultValue: T): Promise<T> {
    try {
      const result: { value: string } = await FirebaseRemoteConfig.getString({ key });
      if (!result.value) {
        return defaultValue;
      }
      const parsed: unknown = JSON.parse(result.value);
      if (Array.isArray(defaultValue) && !Array.isArray(parsed)) {
        return defaultValue;
      }
      return parsed as T;
    } catch {
      return defaultValue;
    }
  }
}
